import type { APIKeyConfig } from "@/types/api-key";

type WavespeedSubmissionResult = {
  requestId?: string;
  payload: any;
};

// Wavespeed endpoints per model
function getWavespeedEndpoint(model: string): { url: string; body: any } {
  // Default shape; caller will override prompt and some params
  const base = "https://api.wavespeed.ai/api/v3";
  const m = (model || "").toLowerCase();

  if (m.includes("gemini-3-pro-image") || m.includes("nano-banana")) {
    return {
      url: `${base}/google/gemini-3-pro-image/text-to-image`,
      body: {
        enable_base64_output: true,
        enable_sync_mode: true,
      },
    };
  }

  switch (model) {
    case "google/gemini-2.5-flash-image/text-to-image": {
      return {
        url: `${base}/google/gemini-2.5-flash-image/text-to-image`,
        body: {
          // Prompt will be injected by caller
          output_format: "png",
          enable_sync_mode: false,
          enable_base64_output: true,
        },
      };
    }
    case "wavespeed-ai/hunyuan-image-2.1": {
      return {
        url: `${base}/wavespeed-ai/hunyuan-image-2.1`,
        body: {
          enable_base64_output: true,
          enable_sync_mode: true,
        },
      };
    }
    case "wavespeed-ai/hunyuan-image-3": {
      return {
        url: `${base}/wavespeed-ai/hunyuan-image-3`,
        body: {
          enable_base64_output: true,
          enable_sync_mode: true,
        },
      };
    }
    case "alibaba/wan-2.5/text-to-image": {
      return {
        url: `${base}/alibaba/wan-2.5/text-to-image`,
        body: {
          enable_base64_output: true,
          enable_sync_mode: true,
        },
      };
    }
    case "wavespeed-ai/qwen-image/text-to-image": {
      return {
        url: `${base}/wavespeed-ai/qwen-image/text-to-image`,
        body: {
          enable_base64_output: true,
          enable_sync_mode: true,
        },
      };
    }
    case "wavespeed-ai/flux-dev": {
      return {
        url: `${base}/wavespeed-ai/flux-dev`,
        body: {
          // Prompt will be injected by caller
          resolution: "1k",
          num_images: 1,
          enable_base64_output: true,
          enable_sync_mode: true,
        },
      };
    }
    case "google/imagen4": {
      return {
        url: `${base}/google/imagen4`,
        body: {
          // Prompt will be injected by caller
          aspect_ratio: "1:1",
          resolution: "1k",
          num_images: 1,
          negative_prompt: "",
          enable_base64_output: true,
        },
      };
    }
    case "bytedance/seedream-v4": {
      return {
        url: `${base}/bytedance/seedream-v4`,
        body: {
          // Prompt will be injected by caller
          size: "4096*4096",
          enable_base64_output: true,
          enable_sync_mode: true,
        },
      };
    }
    case "bytedance/seedream-v4.5": {
      return {
        url: `${base}/bytedance/seedream-v4.5`,
        body: {
          size: "4096*4096",
          enable_base64_output: true,
          enable_sync_mode: true,
        },
      };
    }
    case "google/nano-banana-pro": {
      return {
        url: `${base}/google/gemini-3-pro-image/text-to-image`,
        body: {
          enable_base64_output: true,
          enable_sync_mode: true,
        },
      };
    }
    case "wavespeed-ai/flux-2-pro": {
      return {
        url: `${base}/wavespeed-ai/flux-2-pro/text-to-image`,
        body: {
          enable_base64_output: true,
          enable_sync_mode: false,
          seed: -1,
        },
      };
    }
    case "wavespeed-ai/flux-2-flex": {
      return {
        url: `${base}/wavespeed-ai/flux-2-flex/text-to-image`,
        body: {
          enable_base64_output: true,
          enable_sync_mode: false,
          seed: -1,
        },
      };
    }
    case "wavespeed-ai/flux-2-dev": {
      return {
        url: `${base}/wavespeed-ai/flux-2-dev/text-to-image`,
        body: {
          enable_base64_output: true,
          enable_sync_mode: false,
          seed: -1,
        },
      };
    }
    default: {
      // Fallback to imagen4 as a safe default contract
      return {
        url: `${base}/google/imagen4`,
        body: {
          aspect_ratio: "1:1",
          resolution: "1k",
          num_images: 1,
          negative_prompt: "",
          enable_base64_output: true,
        },
      };
    }
  }
}

function isLikelyBareBase64(value: string): boolean {
  if (value.length < 200) return false;
  // Strip potential whitespace/newlines before testing
  const normalized = value.replace(/\s+/g, "");
  return /^[A-Za-z0-9+/]+={0,2}$/.test(normalized);
}

async function normalizeImageString(value: string, signal?: AbortSignal, seenUrls?: Set<string>): Promise<string | null> {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('data:image/')) {
    return trimmed;
  }
  if (isLikelyBareBase64(trimmed)) {
    return `data:image/png;base64,${trimmed.replace(/\s+/g, '')}`;
  }
  if (/^https?:\/\//i.test(trimmed)) {
    const cache = seenUrls ?? new Set<string>();
    if (cache.has(trimmed)) return null;
    cache.add(trimmed);
    try {
      return await fetchUrlToDataUri(trimmed, signal);
    } catch {
      return null;
    }
  }
  return null;
}

async function resolveImageData(payload: any, signal?: AbortSignal): Promise<string | null> {
  if (!payload || typeof payload !== 'object') return null;

  const seenObjects = new Set<any>();
  const queue: any[] = [];
  const seenUrls = new Set<string>();

  const enqueue = (obj: any) => {
    if (!obj || typeof obj !== 'object') return;
    if (seenObjects.has(obj)) return;
    seenObjects.add(obj);
    queue.push(obj);
  };

  enqueue(payload);
  if (payload.data && typeof payload.data === 'object') enqueue(payload.data);

  while (queue.length > 0) {
    const node = queue.shift();
    if (!node || typeof node !== 'object') continue;

    // Direct string candidates on the current node
    const directKeys = [
      'image',
      'image_url',
      'imageUrl',
      'url',
      'uri',
      'result_url',
      'resultUrl',
      'signed_url',
      'asset_url',
      'contentUrl',
    ];
    for (const key of directKeys) {
      const value = (node as any)[key];
      if (typeof value === 'string') {
        const normalized = await normalizeImageString(value, signal, seenUrls);
        if (normalized) return normalized;
      }
    }

    // URL collections - but skip API endpoints (like wavespeed polling URLs)
    if (node.urls && typeof node.urls === 'object') {
      for (const [key, value] of Object.entries(node.urls)) {
        // Skip known API endpoint keys
        if (key === 'get' || key === 'cancel' || key === 'status') continue;
        if (typeof value === 'string') {
          // Skip URLs that look like API endpoints
          if (value.includes('/api/') || value.includes('/predictions/')) continue;
          const normalized = await normalizeImageString(value, signal, seenUrls);
          if (normalized) return normalized;
        }
      }
    }

    // Gather arrays that might contain image info
    const arrayCandidates = [
      node.outputs,
      node.output,
      node.images,
      node.results,
      node.data,
      node.assets,
    ];
    for (const candidate of arrayCandidates) {
      if (Array.isArray(candidate)) {
        for (const item of candidate) {
          if (typeof item === 'string') {
            const normalized = await normalizeImageString(item, signal, seenUrls);
            if (normalized) return normalized;
            continue;
          }
          if (item && typeof item === 'object') {
            enqueue(item);
          }
        }
      }
    }

    // Nested objects to inspect next
    const objectCandidates = [
      (node as any).data,
      (node as any).result,
      (node as any).prediction,
      (node as any).response,
      (node as any).metadata,
      (node as any).details,
    ];
    objectCandidates.forEach(enqueue);
  }

  // Attempt regex-based detection for data:image or http URLs inside JSON string
  try {
    const serialized = JSON.stringify(payload);
    const dataUriMatch = serialized.match(/data:image\/(?:png|jpe?g|webp);base64,[A-Za-z0-9+/=]+/i);
    if (dataUriMatch?.[0]) {
      return dataUriMatch[0];
    }
    // Use RegExp constructors to avoid parser issues with backslashes in regex literals
    const urlRegexEscaped = new RegExp('https?:\\\\/\\\\/[^"\\\\]+', 'g');
    const urlRegexPlain = new RegExp('https?://[^"\\\\\n\r\s]+', 'g');
    const urlMatches = serialized.match(urlRegexEscaped) ?? serialized.match(urlRegexPlain);
    if (urlMatches) {
      for (const raw of urlMatches) {
        const normalizedUrl = raw.replace(/\\\//g, '/');
        // Skip API endpoints - they are not image URLs
        if (normalizedUrl.includes('/api/') || normalizedUrl.includes('/predictions/')) continue;
        // Skip truncated URLs (less than 20 chars after protocol)
        if (normalizedUrl.replace(/^https?:\/\//, '').length < 20) continue;
        const normalized = await normalizeImageString(normalizedUrl, signal, seenUrls);
        if (normalized) return normalized;
      }
    }
  } catch {}

  const fallbackB64 = tryExtractBase64FromJson(payload);
  if (fallbackB64) {
    return `data:image/png;base64,${fallbackB64}`;
  }

  return null;
}

function extractStatus(payload: any): string {
  if (!payload || typeof payload !== 'object') return '';
  const candidates = [
    (payload as any).status,
    (payload as any).state,
    (payload as any).result?.status,
    (payload as any).result?.state,
    (payload as any).data?.status,
    (payload as any).data?.state,
  ];
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim().toLowerCase();
    }
  }
  if ((payload as any).success === true) return 'success';
  return '';
}

async function wavespeedSubmit(apiKey: string, url: string, body: any, prompt: string, signal?: AbortSignal): Promise<WavespeedSubmissionResult> {
  // Insert prompt field in a tolerant way
  const payload: any = { ...body };
  if (payload.prompt === undefined) payload.prompt = prompt;
  // Some endpoints might use 'text' or 'input' alternatively; include them for safety without harm
  if (payload.text === undefined) payload.text = prompt;
  if (payload.input === undefined) payload.input = prompt;

  const resp = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${apiKey}`,
    },
    body: JSON.stringify(payload),
    signal,
  });
  if (!resp.ok) {
    let msg = `HTTP ${resp.status}`;
    try {
      const err = await resp.json();
      msg = String(err?.error?.message || err?.message || msg);
    } catch {}
    throw new Error(msg);
  }
  const json = await resp.json();
  // Try to extract request id from multiple possible fields
  const requestId = json?.requestId || json?.request_id || json?.id || json?.task_id || json?.taskId || json?.data?.requestId || json?.data?.id;
  return {
    requestId: typeof requestId === 'string' && requestId.trim() ? requestId : undefined,
    payload: json,
  };
}

async function wavespeedGetResult(apiKey: string, requestId: string, signal?: AbortSignal): Promise<any> {
  const endpoints = [
    `https://api.wavespeed.ai/api/v3/predictions/${encodeURIComponent(requestId)}/result`,
    `https://api.wavespeed.ai/api/v3/predictions/${encodeURIComponent(requestId)}`,
  ];

  for (const endpoint of endpoints) {
    const resp = await fetch(endpoint, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${apiKey}`,
      },
      signal,
    });
    if (resp.status === 204 || resp.status === 202) {
      return { status: 'pending' };
    }
    if (resp.status === 404) {
      // Try the next endpoint variant
      continue;
    }
    if (!resp.ok) {
      let msg = `HTTP ${resp.status}`;
      try {
        const err = await resp.json();
        msg = String(err?.error?.message || err?.message || msg);
      } catch {}
      throw new Error(msg);
    }
    const contentType = resp.headers.get('content-type') || '';
    if (!contentType.toLowerCase().includes('application/json')) {
      continue;
    }
    try {
      const json = await resp.json();
      if (json) return json;
    } catch {
      continue;
    }
  }

  return { status: 'pending' };
}

function tryExtractBase64FromJson(obj: any): string | null {
  // Look for explicit b64 fields first
  const b64 = obj?.b64_json || obj?.data?.[0]?.b64_json;
  if (typeof b64 === "string" && b64.length > 100) return b64;
  // Scan serialized JSON for data:image URIs
  try {
    const s = JSON.stringify(obj);
    const m = s.match(/data:image\/(png|jpe?g|webp);base64,[A-Za-z0-9+/=]+/i);
    if (m) return m[0].replace(/^data:image\/\w+;base64,/, "");
  } catch {}
  return null;
}

async function fetchUrlToDataUri(url: string, signal?: AbortSignal): Promise<string> {
  const resp = await fetch(url, { signal });
  if (!resp.ok) throw new Error(`Failed to fetch image URL: HTTP ${resp.status}`);
  
  // Get the blob and detect correct MIME type
  const blob = await resp.blob();
  const arrayBuffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(arrayBuffer);
  
  // Detect image type by magic bytes or URL extension
  let mimeType = blob.type;
  
  // Check for PNG magic bytes: 89 50 4E 47 (‰PNG)
  if (bytes.length >= 4 && bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4E && bytes[3] === 0x47) {
    mimeType = 'image/png';
  }
  // Check for JPEG magic bytes: FF D8 FF
  else if (bytes.length >= 3 && bytes[0] === 0xFF && bytes[1] === 0xD8 && bytes[2] === 0xFF) {
    mimeType = 'image/jpeg';
  }
  // Check for WebP magic bytes: RIFF....WEBP
  else if (bytes.length >= 12 && 
           bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46 &&
           bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50) {
    mimeType = 'image/webp';
  }
  // Fallback: detect by URL extension if magic bytes don't match
  else if (mimeType === 'binary/octet-stream' || mimeType === 'application/octet-stream' || !mimeType || !mimeType.startsWith('image/')) {
    const urlLower = url.toLowerCase();
    if (urlLower.endsWith('.png') || urlLower.includes('.png?') || urlLower.includes('.png#')) {
      mimeType = 'image/png';
    } else if (urlLower.endsWith('.jpg') || urlLower.endsWith('.jpeg') || urlLower.includes('.jpg?') || urlLower.includes('.jpeg?')) {
      mimeType = 'image/jpeg';
    } else if (urlLower.endsWith('.webp') || urlLower.includes('.webp?')) {
      mimeType = 'image/webp';
    } else {
      // Default to PNG if we can't determine
      mimeType = 'image/png';
    }
  }
  
  // Create a new blob with the correct MIME type
  const correctedBlob = new Blob([arrayBuffer], { type: mimeType });
  
  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(correctedBlob);
  });
}

export async function generateImageWavespeed(
  prompt: string,
  api: Pick<APIKeyConfig, "apiKey" | "model" | "temperature">,
  opts?: { signal?: AbortSignal }
): Promise<{ imageData: string }> {
  const { url, body } = getWavespeedEndpoint(api.model);
  const submission = await wavespeedSubmit(api.apiKey, url, body, prompt, opts?.signal);

  const immediateImage = await resolveImageData(submission.payload, opts?.signal);
  if (immediateImage) {
    return { imageData: immediateImage };
  }

  const requestId = submission.requestId;
  if (!requestId) {
    throw new Error("Wavespeed: missing requestId in submission response");
  }

  // Poll result up to ~90s
  const started = Date.now();
  const timeoutMs = 90000;
  let delay = 1000;
  while (Date.now() - started < timeoutMs) {
    const result = await wavespeedGetResult(api.apiKey, requestId, opts?.signal);
    // Some endpoints wrap payload under `data`, others return flat fields
    const status = extractStatus(result);

    const resolvedImage = await resolveImageData(result, opts?.signal);
    if (resolvedImage) {
      return { imageData: resolvedImage };
    }

    if (status === "failed" || status === "error") {
      const msg = String(result?.error?.message || result?.message || "Image generation failed");
      throw new Error(msg);
    }
    if (status === "completed" || status === "succeeded" || status === "success") {
      throw new Error("Wavespeed: completed but no image data found");
    }
    await new Promise((r) => setTimeout(r, delay));
    delay = Math.min(3000, Math.floor(delay * 1.5));
  }
  throw new Error("Wavespeed: image generation timed out");
}

export async function validateWavespeedKey(config: { apiKey: string; model: string }): Promise<{ isValid: boolean; error?: string }> {
  // Wavespeed deployments do not expose discovery endpoints like /models or /predictions.
  // Basic key format validation: must be a non-empty string.
  // Actual authentication errors will surface during image generation.
  if (!config.apiKey || typeof config.apiKey !== 'string' || config.apiKey.trim().length === 0) {
    return { isValid: false, error: 'API key is required' };
  }
  
  // Proceed optimistically - actual generation will surface errors if the key is invalid
  return { isValid: true };
}

function splitDataUri(dataUri: string): { mimeType: string; base64: string } {
  const trimmed = String(dataUri || '').trim();
  const m = trimmed.match(/^data:([^;]+);base64,(.+)$/i);
  if (m && m[1] && m[2]) {
    return { mimeType: m[1], base64: m[2] };
  }
  const cleaned = trimmed.replace(/^data:image\/[A-Za-z0-9.+-]+;base64,/i, "");
  return { mimeType: "image/png", base64: cleaned };
}

function getWavespeedEditEndpoint(model: string): { url: string; body: any } {
  const base = "https://api.wavespeed.ai/api/v3";
  const m = (model || '').toLowerCase();
  if (m.includes('gemini-2.5-flash-image') || m.includes('gemini-3-pro-image') || m.includes('google/nano-banana-pro')) {
    return {
      url: `${base}/google/gemini-3-pro-image/edit`,
      body: { enable_base64_output: true, enable_sync_mode: true },
    };
  }
  if (m.includes('seedream')) {
    const seedreamEndpoint = m.includes('4.5') ? 'bytedance/seedream-v4.5' : 'bytedance/seedream-v4';
    // SeeDream edit API requires:
    // - images: array of URLs (not base64)
    // - enable_base64_output: false (returns URL)
    // - enable_sync_mode: false (async mode)
    return {
      url: `${base}/${seedreamEndpoint}/edit`,
      body: { enable_base64_output: false, enable_sync_mode: false, size: '2048*2048' },
    };
  }
  if (m.includes('qwen-image')) {
    return {
      url: `${base}/wavespeed-ai/qwen-image/edit`,
      body: { enable_base64_output: true, enable_sync_mode: true },
    };
  }
  if (m.includes('hunyuan-image-3')) {
    return {
      url: `${base}/wavespeed-ai/hunyuan-image-3/edit`,
      body: { enable_base64_output: true, enable_sync_mode: true },
    };
  }
  if (m.includes('flux-2-pro')) {
    return {
      url: `${base}/wavespeed-ai/flux-2-pro/edit`,
      body: { enable_base64_output: false, enable_sync_mode: false, seed: -1 },
    };
  }
  if (m.includes('flux-2-flex')) {
    return {
      url: `${base}/wavespeed-ai/flux-2-flex/edit`,
      body: { enable_base64_output: false, enable_sync_mode: false, seed: -1 },
    };
  }
  if (m.includes('flux-2-dev')) {
    return {
      url: `${base}/wavespeed-ai/flux-2-dev/edit`,
      body: { enable_base64_output: false, enable_sync_mode: false, seed: -1 },
    };
  }
  return {
    url: `${base}/google/gemini-3-pro-image/edit`,
    body: { enable_base64_output: true, enable_sync_mode: true },
  };
}

function isSeedreamModel(model: string): boolean {
  return (model || '').toLowerCase().includes('seedream');
}

export async function generateImageEditWavespeed(
  referenceImageData: string | string[],
  instruction: string,
  api: Pick<APIKeyConfig, 'apiKey' | 'model' | 'temperature'>,
  opts?: { signal?: AbortSignal }
): Promise<{ imageData: string }>{
  const { url, body } = getWavespeedEditEndpoint(api.model);
  const isSeedream = isSeedreamModel(api.model);
  
  // Handle array of reference images
  const referenceArray = Array.isArray(referenceImageData) ? referenceImageData : [referenceImageData];
  
  // Check if all references are URLs or data URIs
  const allUrls = referenceArray.every(ref => /^https?:\/\//i.test(ref));
  const allDataUris = referenceArray.every(ref => /^data:image\//i.test(ref));
  
  const payload = (() => {
    // SeeDream edit API requires ONLY the `images` field with URLs
    // It does NOT accept base64 data URIs or the `image`/`input_image` fields
    if (isSeedream) {
      if (allUrls) {
        // Perfect - SeeDream wants URLs
        return {
          ...body,
          prompt: instruction,
          images: referenceArray,
        };
      }
      // SeeDream requires URLs - data URIs are not supported
      // This should only happen if the image was never stored in Firebase Storage
      // (e.g., a freshly generated image that hasn't been saved yet)
      throw new Error(
        'SeeDream image editing requires publicly accessible image URLs. ' +
        'The reference image appears to be stored locally (not in cloud storage). ' +
        'Please save the character first to upload images to cloud storage, then try again. ' +
        'Alternatively, use a different image model (e.g., Gemini 3 Pro Image) that supports local images.'
      );
    }
    
    if (allUrls) {
      // Send URLs directly; many edit endpoints accept URL(s)
      return {
        ...body,
        prompt: instruction,
        image: referenceArray[0],
        input_image: referenceArray[0],
        images: referenceArray,
      };
    }
    
    if (allDataUris) {
      const processedRefs = referenceArray.map(ref => {
        const { mimeType, base64 } = splitDataUri(ref);
        return { dataUri: `data:${mimeType};base64,${base64}`, base64 };
      });
      
      return {
        ...body,
        prompt: instruction,
        // Provide commonly-accepted fields for various edit endpoints
        image: processedRefs[0].dataUri,
        input_image: processedRefs[0].dataUri,
        image_base64: processedRefs[0].base64,
        images: processedRefs.map(r => r.dataUri),
      };
    }
    
    // Mixed case: convert all to data URIs
    const processedRefs = referenceArray.map(ref => {
      if (/^https?:\/\//i.test(ref)) {
        // For URLs in mixed case, we'll pass them as-is (best effort)
        return { dataUri: ref, base64: '' };
      }
      const { mimeType, base64 } = splitDataUri(ref);
      return { dataUri: `data:${mimeType};base64,${base64}`, base64 };
    });
    
    return {
      ...body,
      prompt: instruction,
      image: processedRefs[0].dataUri,
      input_image: processedRefs[0].dataUri,
      image_base64: processedRefs[0].base64 || undefined,
      images: processedRefs.map(r => r.dataUri),
    };
  })();
  const submission = await wavespeedSubmit(api.apiKey, url, payload, instruction, opts?.signal);
  const immediateImage = await resolveImageData(submission.payload, opts?.signal);
  if (immediateImage) return { imageData: immediateImage };
  const requestId = submission.requestId;
  if (!requestId) throw new Error('Wavespeed: missing requestId for edit task');
  const started = Date.now();
  const timeoutMs = 90000;
  let delay = 1000;
  while (Date.now() - started < timeoutMs) {
    const result = await wavespeedGetResult(api.apiKey, requestId, opts?.signal);
    const status = extractStatus(result);
    const resolvedImage = await resolveImageData(result, opts?.signal);
    if (resolvedImage) return { imageData: resolvedImage };
    if (status === 'failed' || status === 'error') {
      const msg = String(result?.error?.message || result?.message || 'Image edit failed');
      throw new Error(msg);
    }
    if (status === 'completed' || status === 'succeeded' || status === 'success') {
      throw new Error('Wavespeed: edit completed but no image data found');
    }
    await new Promise(r => setTimeout(r, delay));
    delay = Math.min(3000, Math.floor(delay * 1.5));
  }
  throw new Error('Wavespeed: image edit timed out');
}
